import React, { useEffect, useRef, useState } from 'react';
import { offers, calendlyUrl } from '../data/mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Send, Calendar, Mail, User, Building, MessageSquare, CheckCircle2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { trackEvent, EventTypes } from '../services/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showCalendly, setShowCalendly] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    offer: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleOfferChange = (value) => {
    setFormData((prev) => ({ ...prev, offer: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company || null,
          offer: formData.offer || null,
          message: formData.message,
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit form');
      }
      
      // Track successful contact form submission
      trackEvent(EventTypes.CONTACT_FORM, 'contact_form_submit', 'landing', { offer: formData.offer });
      
      setIsSubmitted(true);
      toast.success('Message envoyé avec succès !', {
        description: 'Nous vous recontacterons dans les plus brefs délais.',
      });
      
      setTimeout(() => {
        setFormData({ name: '', email: '', company: '', offer: '', message: '' });
        setIsSubmitted(false);
      }, 3000);
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Erreur lors de l\'envoi', {
        description: 'Veuillez réessayer plus tard.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCalendlyToggle = (isCalendly) => {
    setShowCalendly(isCalendly);
    if (isCalendly) {
      trackEvent(EventTypes.CALENDLY_OPEN, 'calendly_toggle', 'landing');
    }
  };

  return (
    <section id="contact" ref={sectionRef} className="relative py-32 bg-[#0a0a0b] overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#3dd3ee]/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-[#c8aff0]/10 rounded-full blur-[150px]" />
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div
          className={`text-center mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-xl rounded-full border border-white/10 text-sm font-medium text-white/70 mb-6">
            <Sparkles className="w-4 h-4 text-[#fad24b]" />
            Parlons de votre projet
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Contactez-
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, #3dd3ee, #c8aff0)' }}>
              nous
            </span>
          </h2>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            Prenez rendez-vous ou envoyez-nous un message
          </p>
        </div>

        {/* Toggle Buttons */}
        <div
          className={`flex justify-center gap-4 mb-12 transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <button
            onClick={() => handleCalendlyToggle(false)}
            className={`group relative px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center gap-2 overflow-hidden ${
              !showCalendly
                ? 'bg-gradient-to-r from-[#3dd3ee] via-[#6ee7f0] to-[#c8aff0] text-[#0a0a0b] shadow-lg shadow-[#3dd3ee]/30'
                : 'bg-white/5 text-white/70 border border-white/10 hover:bg-white/10 hover:border-[#3dd3ee]/30'
            }`}
          >
            {!showCalendly && <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500" />}
            <MessageSquare className="relative z-10 w-4 h-4" />
            <span className="relative z-10">Formulaire</span>
          </button>
          <button
            onClick={() => handleCalendlyToggle(true)}
            className={`group relative px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center gap-2 overflow-hidden ${
              showCalendly
                ? 'bg-gradient-to-r from-[#3dd3ee] via-[#6ee7f0] to-[#c8aff0] text-[#0a0a0b] shadow-lg shadow-[#3dd3ee]/30'
                : 'bg-white/5 text-white/70 border border-white/10 hover:bg-white/10 hover:border-[#3dd3ee]/30'
            }`}
          >
            {showCalendly && <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500" />}
            <Calendar className="relative z-10 w-4 h-4" />
            <span className="relative z-10">Calendly</span>
          </button>
        </div>

        {/* Content */}
        <div
          className={`max-w-4xl mx-auto transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {showCalendly ? (
            <div className="bg-white rounded-3xl overflow-hidden shadow-2xl">
              <iframe
                src={calendlyUrl}
                width="100%"
                height="700"
                frameBorder="0"
                title="Calendly"
                className="w-full"
              />
            </div>
          ) : (
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 lg:p-12 border border-white/10">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-[#b4dc19]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-10 h-10 text-[#b4dc19]" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">Message envoyé !</h3>
                  <p className="text-white/60">Nous vous recontacterons dans les plus brefs délais.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-white/70 flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Nom complet
                      </label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Jean Dupont"
                        required
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#3dd3ee] focus:ring-[#3dd3ee]/20"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-white/70 flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        Email
                      </label>
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="jean@entreprise.com"
                        required
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#3dd3ee] focus:ring-[#3dd3ee]/20"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-white/70 flex items-center gap-2">
                        <Building className="w-4 h-4" />
                        Entreprise
                      </label>
                      <Input
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        placeholder="Nom de l'entreprise"
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#3dd3ee] focus:ring-[#3dd3ee]/20"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-white/70 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Type de projet
                      </label>
                      <Select value={formData.offer} onValueChange={handleOfferChange}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-white">
                          <SelectValue placeholder="Sélectionnez" />
                        </SelectTrigger>
                        <SelectContent>
                          {offers.map((offer) => (
                            <SelectItem key={offer.id} value={offer.id}>
                              {offer.name}
                            </SelectItem>
                          ))}
                          <SelectItem value="other">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-white/70 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Votre message
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Décrivez votre projet ou vos besoins..."
                      rows={5}
                      required
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#3dd3ee] focus:ring-[#3dd3ee]/20 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="group relative w-full bg-gradient-to-r from-[#3dd3ee] via-[#6ee7f0] to-[#c8aff0] text-[#0a0a0b] font-semibold py-6 text-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-[#3dd3ee]/30 disabled:opacity-50 rounded-xl overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                    {isSubmitting ? (
                      <div className="relative z-10 flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-2 border-[#0a0a0b]/30 border-t-[#0a0a0b] rounded-full animate-spin" />
                        Envoi en cours...
                      </div>
                    ) : (
                      <div className="relative z-10 flex items-center justify-center gap-2">
                        <Send className="w-5 h-5" />
                        Envoyer le message
                      </div>
                    )}
                  </Button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Contact;
